import React, { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import '../../styles/components/_codeEditor.scss';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import FormLabel from '@mui/material/FormLabel';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Slide from '@mui/material/Slide';
import Box from '@mui/material/Box';

import FullscreenIcon from '@mui/icons-material/Fullscreen';
import Preview from '@mui/icons-material/Preview';
import CloseIcon from '@mui/icons-material/Close';

import { safeStringifyJSON, validateAndParseJson } from '../../utils/jsonValidator';

// Animación de entrada para el modal
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export const MultiLanguageEditor = ({
    value,
    onChange,
    dfLanguage = 'json',
    htCtrl = '400',
    errorInputs = {},
    field,
    togglePreview,
    showFullscreenButton=true
}) => {
    const editorRef = useRef(null);
    const [editorValue, setEditorValue] = useState(() =>
        dfLanguage === 'json' ? safeStringifyJSON(value) : value
    );
    const [isValid, setIsValid] = useState(true);
    const [isFocused, setIsFocused] = useState(false);
    const [openFullscreen, setOpenFullscreen] = useState(false);

    const handleEditorChange = (val) => {
        setEditorValue(val);
        if (dfLanguage === 'json') {
            const { valid, json } = validateAndParseJson(val);
            if (valid) {
                onChange(json);
                setIsValid(true);
            } else {
                setIsValid(false);
            }
        } else {
            onChange(val);
            setIsValid(true);
        }
    };

    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        editor.onDidFocusEditorWidget(() => setIsFocused(true));
        editor.onDidBlurEditorWidget(() => setIsFocused(false));
    };

    useEffect(() => {
        setEditorValue(dfLanguage === 'json' ? safeStringifyJSON(value) : value);
    }, [value, dfLanguage]);

    const handleToggleFullscreen = () => setOpenFullscreen(!openFullscreen);

    const renderEditor = (height) => (
        <Editor
            height={height}
            defaultLanguage={dfLanguage}
            value={editorValue}
            onChange={handleEditorChange}
            onMount={handleEditorDidMount}
            options={{
                wordWrap: "on",
                scrollBeyondLastLine: false,
                minimap: { enabled: false },
                automaticLayout: true,
                fontSize: openFullscreen ? 16 : 14,
            }}
        />
    );

    return (
        <FormControl fullWidth error={Boolean(errorInputs[field?.id]?.isError)}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                {field && <FormLabel sx={{ mb: 0 }}> {field?.label} {field?.required && " *"} </FormLabel>}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0 }}>
                    {togglePreview && typeof togglePreview === 'function' && (
                        <Tooltip title="Preview" placement="top">
                            <Preview
                            onClick={togglePreview}
                            sx={{
                                height: 25,
                                cursor: "pointer",
                                color: '#6728AF'
                            }}
                            />
                        </Tooltip>
                    )}
                    { showFullscreenButton && 
                        <FullscreenIcon
                            onClick={handleToggleFullscreen}
                            sx={{
                                height: 25,
                                cursor: "pointer",
                                color: '#6728AF'
                            }}
                        />
                    }
                </Box>
            </Box>
            <Box
                sx={{
                    border: "1px solid",
                    borderColor: errorInputs[field?.id]?.isError ? "red" : isValid ? "divider" : "red",
                    borderRadius: 1,
                    overflow: "hidden",
                    height: `${htCtrl}px`,
                    position: 'relative'
                }}
                className={`editor-container ${!isValid ? "invalid" : isFocused ? "focused" : ""}`}
            >
                {renderEditor("100%")}
            </Box>

            <Dialog
                fullScreen
                open={openFullscreen}
                onClose={handleToggleFullscreen}
                TransitionComponent={Transition}
            >
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            {field?.label || 'Editor:'}
                        </Typography>
                        <IconButton edge="start" color="inherit" onClick={handleToggleFullscreen}>
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <Box sx={{ flexGrow: 1, height: 'calc(100vh - 64px)' }}>
                    {renderEditor("100%")}
                </Box>
            </Dialog>


            {errorInputs[field?.id]?.isError && (
                <FormHelperText>{errorInputs[field?.id]?.messageError}</FormHelperText>
            )}
        </FormControl>
    );
};
